/**
 * The one thing this console needs to know about a query that `isPending` does
 * not tell it.
 *
 * Its own module, and not part of `shell.tsx`, for the reason
 * `environment-context.ts` gives: Fast Refresh is per-module, and a file that
 * exports both components and the functions around them reloads the whole app
 * on every edit instead of swapping the component.
 */

/**
 * Whether this query is disabled rather than loading.
 *
 * TanStack Query v5 has two statuses, not one. `status` answers *do I have
 * data*, and `fetchStatus` answers *is a request in flight*. A query with
 * `enabled: false` has never had data and never will until something enables
 * it, so it reports `isPending: true` **forever**, with `fetchStatus: "idle"`.
 * It is not loading; it is never going to load.
 *
 * Read `isPending` alone and every environment-scoped screen reached from a
 * project with no environment draws a skeleton for as long as it is open — a
 * skeleton that resolves into nothing, where the honest answer is one sentence.
 *
 * All three conditions, in this order. `isPending` alone is true of a disabled
 * query forever. `fetchStatus === "idle"` alone is true between refetches of a
 * query that already has data. And `data === undefined` keeps a query that was
 * enabled, answered, and has since been disabled — a filter turned off, a
 * selection cleared — showing what it fetched rather than blanking.
 *
 * `Async` applies this for every screen that renders through it, which is
 * nearly all of them. This is exported for the few that check `isPending`
 * themselves before an early return — `routes/loops.tsx` is one — because a
 * second hand-written copy of the test is a second chance to write it wrong.
 */
export function neverAsked(query: {
  data: unknown;
  isPending: boolean;
  fetchStatus?: "fetching" | "paused" | "idle";
}): boolean {
  return query.isPending && query.fetchStatus === "idle" && query.data === undefined;
}
